import { styled } from '@linaria/react';
import { Card, Skeleton, Stack } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Measure } from '../../../../api/work-periods-client/work-periods-client.type';
import { formatDate } from '../../../../helpers/date-time-helpers/formatting/formatting';
import { secondaryBase, skyDark, skyLight, skyTight } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import type { KeyProcessMeasuresData } from '../../../../helpers/process-analysis-shared/process-analysis-shared.type';
import type { TaskRecord } from '../../../tasks-table/tasks-table.type';
import { useWorkPeriodsTasksData } from '../../hooks/work-periods-calculation.hooks';
import type { DateRange, WorkPeriod } from '../../work-periods.type';
import { formatKeyMeasuresData } from './helpers/key-measures-section.helpers';

type KeyMeasuresSectionProps = {
  keyMeasuresData: KeyProcessMeasuresData | undefined;
  dateRange: DateRange;
  selectedWorkPeriod: WorkPeriod | null;
  onTasksChange: (tasks: TaskRecord[], title?: string, isLoading?: boolean) => void;
  tasksOpen: boolean;
  calculatedScopeChange: number | null;
  /**
   * When true and no measure data is yet available, renders a grid of skeleton
   * tiles sized identically to the real measure cards so the layout does not
   * shift when data arrives. Ignored once `keyMeasuresData` is populated.
   */
  isLoading?: boolean;
};

const KEY_MEASURES_SKELETON_COUNT = 8;

export function KeyMeasuresSection({
  keyMeasuresData,
  selectedWorkPeriod,
  onTasksChange,
  tasksOpen,
  calculatedScopeChange,
  isLoading = false,
}: KeyMeasuresSectionProps) {
  const { t } = useTranslation();
  const measures = formatKeyMeasuresData(keyMeasuresData, calculatedScopeChange, t);

  const [taskQuery, setTaskQuery] = useState<{
    measureName: Measure;
    measureTitle: string;
    workPeriodId: string;
  } | null>(null);

  const { data: tasksQueryData = [], isFetching } = useWorkPeriodsTasksData(
    [taskQuery?.measureName as Measure],
    formatDate(selectedWorkPeriod?.end_date, { format: 'YYYY-MM-DD' }),
    { enabled: !!taskQuery },
  );

  // Effect to trigger onTasksChange when tasks are loading
  useEffect(() => {
    if (taskQuery && isFetching && onTasksChange) {
      const title = `${taskQuery.measureTitle}`;
      onTasksChange([], title, true);
    }
  }, [taskQuery, isFetching, onTasksChange]);

  // Effect to trigger onTasksChange when tasks data is loaded
  useEffect(() => {
    if (taskQuery && !isFetching && onTasksChange) {
      const title = `${taskQuery.measureTitle}`;
      onTasksChange(tasksQueryData as TaskRecord[], title, false);
    }
  }, [tasksQueryData, taskQuery, isFetching, onTasksChange]);

  const handleMeasureClick = (measureName: string, measureTitle: string) => {
    setTaskQuery({
      workPeriodId: selectedWorkPeriod?.id || '',
      measureName: measureName as Measure,
      measureTitle: measureTitle,
    });
  };

  if (!keyMeasuresData && isLoading) {
    return (
      <Stack
        data-testid="key-measures-section-skeleton"
        role="status"
        aria-busy="true"
        aria-label="Loading key process measures"
      >
        <Stack gap={2} mt={24}>
          <Text weight="bold" size="medium">
            Key Process Measures
          </Text>
          <Text size="small" color={skyDark}>
            Performance metrics for the selected work period
          </Text>
        </Stack>
        <MeasuresGrid>
          {Array.from({ length: KEY_MEASURES_SKELETON_COUNT }).map((_, index) => (
            <SkeletonMeasureCard key={`key-measure-skeleton-${index}`}>
              <Skeleton height={16} width="60%" radius="sm" />
              <Skeleton height={12} width="80%" radius="sm" />
              <Skeleton height={28} width="40%" radius="sm" />
            </SkeletonMeasureCard>
          ))}
        </MeasuresGrid>
      </Stack>
    );
  }

  return keyMeasuresData ? (
    <Stack>
      <Stack gap={2} mt={24}>
        <Text weight="bold" size="medium">
          Key Process Measures
        </Text>
        <Text size="small" color={skyDark}>
          Performance metrics for the selected work period
        </Text>
      </Stack>

      <MeasuresGrid>
        {measures.map((measure) => {
          const isHighlighted = tasksOpen && taskQuery?.measureName === measure.id;
          return (
            <MeasureCard
              key={measure.id}
              $clickable={true}
              $highlighted={isHighlighted}
              onClick={() => handleMeasureClick(measure.id, measure.name)}
            >
              <MeasureHeader>
                <MeasureTitle>{measure.name}</MeasureTitle>
                <MeasureDescription>{measure.description}</MeasureDescription>
              </MeasureHeader>
              <MeasureValueContainer>
                <MeasureValue>{measure.value}</MeasureValue>
              </MeasureValueContainer>
            </MeasureCard>
          );
        })}
      </MeasuresGrid>
    </Stack>
  ) : (
    <Card p="md" withBorder>
      <Text weight="bold" size="medium">
        Key Process Measures
      </Text>
      <Text color="dimmed">Select a work period or date range to view key measures.</Text>
    </Card>
  );
}

const MeasuresGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
`;

const SkeletonMeasureCard = styled.div`
  background-color: ${skyTight};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  min-height: 120px;
  height: auto;
  border: 1px solid ${skyLight};
`;

const MeasureCard = styled.div<{ $clickable: boolean; $highlighted?: boolean }>`
  background-color: ${(props) => (props.$highlighted ? `${secondaryBase}20` : skyTight)};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  min-height: 120px;
  height: auto;
  border: ${(props) => (props.$highlighted ? `2px solid ${secondaryBase}` : `1px solid ${skyLight}`)};
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: all 0.2s ease-in-out;
  box-shadow: ${(props) => (props.$highlighted ? '0 4px 12px rgba(2, 136, 209, 0.3)' : 'none')};

  &:hover {
    box-shadow: ${(props) => {
      if (props.$highlighted) return `0 6px 16px rgba(${secondaryBase}, 0.4)`;
      return props.$clickable ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none';
    }};
    transform: ${(props) => (props.$clickable ? 'translateY(-2px)' : 'none')};
  }
`;

const MeasureTitle = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 16px;
  color: #000000;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const MeasureValue = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #000000;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const MeasureDescription = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
  color: #979c9e;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: center;
`;

const MeasureHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const MeasureValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
